import * as React from "react";
import { useSearchParams } from "react-router";
import TopBarProgress from "react-topbar-progress-indicator";

import { DriveHeader } from "@/views/drive/driveHeader";
import { DriveList } from "@/views/drive/driveList";
import { Pagination } from "@/views/drive/pagination";
import { DriveScopeTabs } from "./DriveScopeTabs";
import { DriveToolbar } from "./DriveToolbar";
import type { DriveScope } from "./types";
import { useUnifiedDriveWorkspace } from "./useUnifiedDriveWorkspace";
import { DriveSkeleton } from "../driveSkeleton";
import { DriveEmptyState, DriveFatalError, DrivePartialError } from "./DriveWorkspaceStates";
import { DriveWorkspaceHeader } from "./DriveWorkspaceHeader";
import { DriveActionBar } from "@/views/drive/components/DriveActionBar";
import { DeleteConfirmationModal } from "@/views/drive/modals/deleteConfirmationModal";
import { useItemSelection } from "@/views/drive/hooks/useItemSelection";
import { getMutableSelectedItems, useMoveDelete } from "@/views/drive/hooks/useMoveDelete";
import type { FileItem, Item } from "@/types/fse";
import { useContractFamiliesAccess } from "@/hooks/use-contract-families-access";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";

interface Props {
  initialScope: DriveScope;
}

export const UnifiedDriveWorkspace: React.FC<Props> = ({ initialScope }) => {
  const workspace = useUnifiedDriveWorkspace({ initialScope });
  const authenticatedUser = useAuthenticatedUser();
  const { hasContractFamilies } = useContractFamiliesAccess();
  const authenticatedUserId = workspace.authenticatedUserId;
  const canMutateItem = React.useCallback((item: Item) => item.owner_id === authenticatedUserId, [authenticatedUserId]);
  const { selectedItemIds, handleItemSelection, clearSelection, handleToggleSelectAll } = useItemSelection({
    items: workspace.rows,
    currentPage: workspace.currentPage,
    itemsPerPage: workspace.itemsPerPage,
    folderId: workspace.rootFolder?.id,
    canSelectItem: canMutateItem,
  });
  const presentSelectedItems = React.useMemo(
    () => getMutableSelectedItems(workspace.rows, selectedItemIds, canMutateItem),
    [canMutateItem, selectedItemIds, workspace.rows],
  );
  const selectedFileItems = React.useMemo(
    () => presentSelectedItems.filter((item): item is FileItem => item.type === "file"),
    [presentSelectedItems],
  );
  const moveDelete = useMoveDelete({ items: workspace.rows, selectedItemIds, clearSelection, canMutateItem });
  const handleMoveItems = moveDelete.handleMoveItems;
  const handleMoveItem = React.useCallback(
    (sourceItem: Item, targetFolder: Parameters<typeof handleMoveItems>[1]) => {
      if (!canMutateItem(sourceItem) || !canMutateItem(targetFolder)) return Promise.resolve();
      return handleMoveItems(sourceItem, targetFolder);
    },
    [canMutateItem, handleMoveItems],
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const clearSearch = React.useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    next.delete("mode");
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  return (
    <div data-testid="drive-view">
      {((workspace.isFetching && !workspace.isLoading) || moveDelete.isMoveFileLoading) && <TopBarProgress />}
      {workspace.rootFolder && (
        <DriveHeader
          parentFolder={null}
          currentFolder={workspace.rootFolder}
          isRootFolder={true}
          askAIButton={true}
          addFilesButton={true}
        />
      )}
      {!workspace.rootFolder && (
        <DriveWorkspaceHeader currentFolder={undefined} parentFolder={null} isRootFolder={true} actions={null} />
      )}
      <DriveScopeTabs scope={workspace.scope} counts={workspace.counts} />
      <DriveToolbar currentSort={workspace.currentSort} onSortChange={workspace.setCurrentSort} />
      {!workspace.fatalError && (
        <DriveActionBar
          selectedItemsCount={presentSelectedItems.length}
          selectedFileFseIds={selectedFileItems.map(item => item.id)}
          selectedFolderFseIds={presentSelectedItems.filter(item => item.type === "folder").map(item => item.id)}
          currentFolderFseId={workspace.rootFolder?.id}
          currentFolderName={workspace.rootFolder?.name}
          canUseAdminBulk={(authenticatedUser.pfRoles ?? []).some(role =>
            ["organization_admin", "superadmin"].includes(role),
          )}
          canGroupFamilies={workspace.scope !== "shared"}
          hasContractFamilies={hasContractFamilies}
          onDeleteClick={moveDelete.handleDeleteClick}
          isDeleteLoading={moveDelete.isDeleteLoading}
          currentSort={workspace.currentSort}
          onSortChange={workspace.setCurrentSort}
          isSearchMode
        />
      )}
      {workspace.partialError && (
        <div className="mb-4">
          <DrivePartialError failedSource={workspace.partialError.source} onRetry={workspace.partialError.retry} />
        </div>
      )}
      {workspace.fatalError ? (
        <DriveFatalError onRetry={workspace.fatalError.retry} />
      ) : workspace.isLoading ? (
        <DriveSkeleton hideHeader hideSearch />
      ) : (
        <>
          <DriveList
            items={workspace.visibleRows}
            emptyState={
              <DriveEmptyState
                query={workspace.query}
                canCreate={workspace.scope !== "shared"}
                onClearSearch={clearSearch}
              />
            }
            usersById={workspace.usersById}
            authenticatedUserId={workspace.authenticatedUserId}
            parentFolder={null}
            isRootFolder={true}
            currentPage={1}
            itemsPerPage={workspace.visibleRows.length || 1}
            currentFolderId={workspace.rootFolder?.id ?? null}
            onMoveItem={handleMoveItem}
            movingItemIds={moveDelete.movingItemIds}
            selectedItemIds={selectedItemIds}
            onItemSelection={handleItemSelection}
            onClearSelection={clearSelection}
            onToggleSelectAll={handleToggleSelectAll}
            canMutateItem={canMutateItem}
          />
          {workspace.rows.length > 0 && (
            <Pagination
              currentPage={workspace.currentPage}
              totalPages={workspace.totalPages}
              onPageChange={workspace.setCurrentPage}
            />
          )}
        </>
      )}
      <DeleteConfirmationModal
        isOpen={moveDelete.showDeleteConfirmation}
        itemCount={presentSelectedItems.length}
        onConfirm={moveDelete.handleConfirmDelete}
        onCancel={moveDelete.handleCancelDelete}
      />
    </div>
  );
};
